
import React, { useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useAdmin } from '@/contexts/admin/AdminContext';
import { useArticleForm } from '@/hooks/admin/articles/useArticleForm';
import { useLocation, useNavigate } from 'react-router-dom';

// Import our form section components
import ContentTabSection from './form/ContentTabSection';
import MetadataTabSection from './form/MetadataTabSection';
import PublishingTabSection from './form/PublishingTabSection';
import ArticleFormHeader from './form/ArticleFormHeader';
import ArticleFormLoading from './form/ArticleFormLoading';
import ArticleFormError from './form/ArticleFormError';
import ArticleFormActions from './form/ArticleFormActions';

const ArticleEditor = () => {
  const { articleTags, articleAuthors, loadArticleTags, loadArticleAuthors, uploadArticleMedia } = useAdmin();
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Log navigation state
    console.log("ArticleEditor mounted, location:", location.pathname, location.state);
    
    // Clean up function will run when component unmounts
    return () => {
      console.log("ArticleEditor unmounting");
    };
  }, [location]);
  
  const {
    isEditMode,
    loading,
    error,
    saving,
    formData,
    formErrors,
    handleSubmit,
    handleCancel: originalHandleCancel,
    handleHeaderImageUpload,
    handleCreateTag
  } = useArticleForm();
  
  // Enhanced cancel handler with improved state preservation
  const handleCancel = () => {
    console.log("Navigating back to admin with articles tab active");
    navigate('/admin', { 
      state: { 
        activeTab: 'articles',
        fromEditor: true,
        timestamp: Date.now() // Help force state updates
      }
    });
  };
  
  React.useEffect(() => {
    const loadRequiredData = async () => {
      try {
        if (articleTags.length === 0) {
          await loadArticleTags();
        }
        
        if (articleAuthors.length === 0) {
          await loadArticleAuthors();
        }
      } catch (error) {
        console.error('Error loading form dependencies:', error);
      }
    };
    
    loadRequiredData();
  }, [articleTags.length, articleAuthors.length, loadArticleTags, loadArticleAuthors]);

  if (loading) {
    return <ArticleFormLoading />;
  }

  if (error) {
    return <ArticleFormError error={error} onCancel={handleCancel} />;
  }

  return (
    <div className="container mx-auto py-6 max-w-5xl">
      <div className="flex items-center mb-6">
        <Button variant="ghost" onClick={handleCancel} className="mr-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <ArticleFormHeader 
          isEditMode={isEditMode}
          title={formData.title}
          onCancel={handleCancel}
        />
      </div>
      
      <div className="bg-card rounded-lg border shadow-sm">
        <Tabs defaultValue="content" className="w-full">
          <div className="px-6 pt-6 border-b">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="metadata">Metadata</TabsTrigger>
              <TabsTrigger value="publishing">Publishing</TabsTrigger>
            </TabsList>
          </div>
          
          <div className="p-6">
            <TabsContent value="content" className="mt-0">
              <ContentTabSection 
                title={formData.title}
                setTitle={formData.setTitle}
                leadText={formData.leadText}
                setLeadText={formData.setLeadText}
                content={formData.content}
                setContent={formData.setContent}
                uploadArticleMedia={uploadArticleMedia}
                formErrors={formErrors}
                articleId={isEditMode ? formData.articleId : undefined}
              />
            </TabsContent>
            
            <TabsContent value="metadata" className="mt-0">
              <MetadataTabSection 
                headerImageUrl={formData.headerImageUrl}
                handleHeaderImageUpload={handleHeaderImageUpload}
                clearHeaderImage={() => formData.setHeaderImageUrl('')}
                authorId={formData.authorId}
                setAuthorId={formData.setAuthorId}
                authors={articleAuthors}
                selectedTags={formData.selectedTags}
                setSelectedTags={formData.setSelectedTags}
                tags={articleTags}
                createArticleTag={handleCreateTag}
                formErrors={formErrors}
              />
            </TabsContent>
            
            <TabsContent value="publishing" className="mt-0">
              <PublishingTabSection 
                status={formData.status}
                setStatus={formData.setStatus}
                publishDate={formData.publishDate}
                setPublishDate={formData.setPublishDate}
                calendarOpen={formData.calendarOpen}
                setCalendarOpen={formData.setCalendarOpen}
                formErrors={formErrors}
              />
            </TabsContent>
          </div>
          
          <ArticleFormActions 
            isEditMode={isEditMode}
            handleSubmit={handleSubmit}
            handleCancel={handleCancel}
            isSubmitting={saving}
          />
        </Tabs>
      </div>
    </div>
  );
};

export default ArticleEditor;
